import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { get, patch, fmtDate, useConfig } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { EventTable } from "@/components/EventTable";

function Detail({ kind, id }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: cfg } = useConfig();
  const { data: o, refetch } = useQuery({ queryKey: [kind, id], queryFn: () => get(`/${kind}/${id}`) });
  const [form, setForm] = useState({});
  if (!o) return <div className="p-6 text-xs">Chargement…</div>;
  const save = async () => { await patch(`/${kind}/${id}`, form); toast.success("Fiche mise à jour"); refetch(); qc.invalidateQueries({ queryKey: [kind] }); };
  const freq = o.frequency_days ? `environ tous les ${Math.round(o.frequency_days)} jour(s)` : "insuffisant pour estimer";
  return (
    <div>
      <SheetHeader className="p-5 border-b border-slate-200 dark:border-slate-800 text-left">
        <div className="overline">{kind === "organizers" ? "Organisateur" : "Lieu"} · {o.commune_name}</div>
        <SheetTitle className="font-display text-xl" data-testid="entity-title">{o.name}</SheetTitle>
        <div className="flex gap-5 text-xs pt-1">
          <div><div className="overline">À venir</div><div className="font-mono text-lg">{o.events_future.length}</div></div>
          <div><div className="overline">Passés</div><div className="font-mono text-lg">{o.events_past.length}</div></div>
          <div><div className="overline">Fréquence historique</div><div className="text-sm">{freq}</div></div>
        </div>
      </SheetHeader>
      <div className="p-5 space-y-5">
        <div className="grid grid-cols-2 gap-2">
          {kind === "organizers" ? (
            <label className="text-[11px] text-slate-500">Type
              <select data-testid="entity-type" defaultValue={o.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full h-8 border border-slate-200 dark:border-slate-700 rounded-[3px] bg-transparent text-xs">
                {(cfg?.organizer_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
              </select></label>
          ) : (
            <label className="text-[11px] text-slate-500">Type de lieu<Input data-testid="entity-type" className="h-8 text-xs" defaultValue={o.type || ""} placeholder="salle, stade, mairie…" onChange={(e) => setForm({ ...form, type: e.target.value })} /></label>
          )}
          <label className="text-[11px] text-slate-500">Site web<Input data-testid="entity-website" className="h-8 text-xs" defaultValue={o.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} /></label>
          <label className="text-[11px] text-slate-500 col-span-2">Adresse<Input className="h-8 text-xs" defaultValue={o.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></label>
          {kind === "organizers" && <label className="text-[11px] text-slate-500 col-span-2">Réseaux sociaux publics (séparés par des virgules)<Input className="h-8 text-xs" defaultValue={(o.socials || []).join(", ")} onChange={(e) => setForm({ ...form, socials: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></label>}
        </div>
        <Button data-testid="entity-save" size="sm" onClick={save}>Enregistrer</Button>
        <div><div className="overline mb-1">Événements à venir</div><div className="panel overflow-x-auto"><EventTable items={o.events_future} compact /></div></div>
        <div><div className="overline mb-1">Historique (événements passés)</div><div className="panel overflow-x-auto"><EventTable items={o.events_past} compact /></div></div>
        <div className="grid grid-cols-2 gap-4">
          <div><div className="overline mb-1">Sources associées</div>{o.sources.map((s) => <a key={s.id} href={s.url} target="_blank" rel="noreferrer" className="block text-xs text-blue-700 dark:text-blue-400 truncate">{s.name}</a>)}</div>
          <div><div className="overline mb-1">{kind === "organizers" ? "Lieux fréquents" : "Organisateurs fréquents"}</div>
            {o.related.map((r) => <button key={r.id} onClick={() => nav(`/${kind === "organizers" ? "lieux" : "organisateurs"}?id=${r.id}`)} className="block text-xs hover:underline">{r.name} <span className="font-mono text-slate-400">×{r.count}</span></button>)}</div>
        </div>
      </div>
    </div>
  );
}

function EntityPage({ kind, title }) {
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState("");
  const { data: cfg } = useConfig();
  const { data } = useQuery({ queryKey: [kind, q], queryFn: () => get(`/${kind}`, { q }) });
  const id = sp.get("id");
  return (
    <div className="p-4 lg:p-6 space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="font-display text-xl font-bold">{title}</h1>
        <span className="font-mono text-xs text-slate-500">{data?.length ?? "…"}</span>
        <Input data-testid={`${kind}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="h-7 w-56 text-xs" />
      </div>
      <div className="panel overflow-x-auto">
        <table className="dense-table w-full" data-testid={`${kind}-table`}>
          <thead><tr><th>Nom</th><th>Type</th><th>Commune</th><th>Site web</th><th>Évts à venir</th><th>Évts total</th><th>Dernier évt</th></tr></thead>
          <tbody>{(data || []).map((o) => (
            <tr key={o.id} data-testid={`${kind}-row-${o.id}`} className="cursor-pointer" onClick={() => setSp({ id: o.id })}>
              <td className="font-medium">{o.name}</td><td className="text-[11px]">{o.type || "—"}</td><td>{o.commune_name}</td>
              <td className="text-[11px] text-blue-700 truncate max-w-[200px]">{o.website || "—"}</td>
              <td className="font-mono">{o.events_upcoming}</td><td className="font-mono">{o.events_total}</td><td className="font-mono text-[11px]">{fmtDate(o.last_event_date)}</td>
            </tr>))}
          </tbody>
        </table>
        {data?.length === 0 && <div className="py-8 text-center text-xs text-slate-400">Base vide : elle s'enrichit automatiquement à chaque collecte.</div>}
      </div>
      <Sheet open={!!id} onOpenChange={(o) => !o && setSp({})}>
        <SheetContent side="right" aria-describedby={undefined} className="w-full sm:max-w-3xl p-0 overflow-y-auto safe-top safe-bottom" data-testid="entity-drawer">{id && cfg && <Detail kind={kind} id={id} />}</SheetContent>
      </Sheet>
    </div>
  );
}

export const Organizers = () => <EntityPage kind="organizers" title="Organisateurs" />;
export const Venues = () => <EntityPage kind="venues" title="Lieux" />;
